import api from "@/helpers/api";
import cataloguerFormService from "@/services/materialCatalogingRequest/cataloguerFormService";

// Aksi per item di tabel item MCR (tombol MCR Item Note, Check Duplicate, Cancel) -- dipakai New_UserForm &
// New_CataloguerForm. Yang SAMA di kedua form (modal, payload cancel/uncancel, patch item hasil update status) ada di sini.
// Yang BEDA per form ditulis di form masing-masing (aturan diwarisi UserForm.vue / CataloguerForm.vue lama):
//   - hasDuplicateSource(item)    : kapan tombol Check Duplicate tampil
//   - cancelVisible(item)         : kapan tombol Cancel tampil
//   - toCheckDuplicateItem(item)  : opsional, bentuk item yang dikirim ke CheckDuplicateModal (default item apa adanya)
// Komponen pemakai wajib mendaftarkan AddNoteItemModal & CheckDuplicateModal di template-nya sendiri.
// Semua perubahan item lewat mutation store `mcr/patchMcrItem` (di tempat) -- objek item terpilih tetap objek yang sama.
const CANCEL_NOTE_PREFIX = "This MCR Item has been canceled because";
const UNCANCEL_NOTE_PREFIX = "This MCR Item has been uncanceled because";

export default {
  data() {
    return {
      noteModal: {
        visible: false,
        item: null,
      },
      checkDuplicateModal: {
        visible: false,
        item: null,
        rowNumber: null,
      },
    };
  },
  computed: {
    actionItems() {
      return this.$store.state.mcr.allMcrItem || [];
    },
    currentUsername() {
      return this.$store.state.auth.user?.namecode || "";
    },
    isUserRole() {
      return (this.$store.state.auth.userHasRoles || []).some((role) => role === "User");
    },
  },
  methods: {
    toCheckDuplicateItem(item) {
      return item;
    },
    openNoteModal(item) {
      this.noteModal.item = item;
      this.noteModal.visible = true;
    },
    onNotesSaved({ mcr_item_code, notes }) {
      this.$store.commit("mcr/patchMcrItem", { mcr_item_code, patch: { emcr_item_note: notes } });
    },
    // CheckDuplicateModal yang mengurus fetch-nya sendiri.
    openCheckDuplicateDialog(item) {
      this.checkDuplicateModal.item = this.toCheckDuplicateItem(item);
      this.checkDuplicateModal.rowNumber = this.actionItems.findIndex((i) => i.mcr_item_code === item.mcr_item_code) + 1;
      this.checkDuplicateModal.visible = true;
    },
    onStatusSaved(data) {
      this.applyItemStatusUpdate(data);
    },
    async onCancelItem(item) {
      if (!item?.mcr_item_code) return;
      const notes = item.emcr_item_note || [];
      const nullStatusNotes = notes.filter((n) => n.status === null);
      const pendingCancelNotes = nullStatusNotes.filter((n) => (n.notes || "").startsWith(CANCEL_NOTE_PREFIX));
      const latestByCreatedAt = (arr) => [...arr].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

      let scenario;
      if (item.status === "canceled") {
        if (nullStatusNotes.length === 0) {
          // Sudah fully canceled (tidak ada note pending) -> aksi ini adalah uncancel dengan alasan.
          scenario = "uncancelWithReason";
        } else if (pendingCancelNotes.length === 0) {
          // Tidak ada note cancel null yang pending (null yang ada milik fitur lain) -> jangan proses.
          return;
        } else {
          scenario = "uncancelRemovePending";
        }
      } else {
        scenario = "cancelWithReason";
      }

      const isUncancel = scenario === "uncancelWithReason" || scenario === "uncancelRemovePending";
      const showInput = scenario === "uncancelWithReason" || scenario === "cancelWithReason";
      const result = await this.confirmCancelItemDialog(isUncancel, "cancel", undefined, showInput);
      if (!result.isConfirmed) return;
      try {
        let emcrItemNote = notes;
        if (scenario === "uncancelRemovePending") {
          const latestPendingCancelNote = latestByCreatedAt(pendingCancelNotes);
          emcrItemNote = notes.filter((n) => n !== latestPendingCancelNote);
        } else {
          const reason = (result.value || "").trim();
          const normalizedReason = reason.charAt(0).toLowerCase() + reason.slice(1);
          const prefix = scenario === "uncancelWithReason" ? UNCANCEL_NOTE_PREFIX : CANCEL_NOTE_PREFIX;
          const baseNotes = scenario === "cancelWithReason"
            ? notes.filter((n) => !(n.status === null && ((n.notes || "").startsWith(CANCEL_NOTE_PREFIX) || (n.notes || "").startsWith(UNCANCEL_NOTE_PREFIX))))
            : notes;
          emcrItemNote = [
            ...baseNotes,
            {
              id: null,
              mcr_item_code: item.mcr_item_code || "",
              notes: `${prefix} ${normalizedReason}`,
              status: null,
              created_by: this.currentUsername,
              updated_by: this.currentUsername,
              created_at: null,
              updated_at: null,
            },
          ];
        }

        const payload = {
          status: "canceled",
          old_status: item.status,
          obj_catalogued_by: item.catalogued_by || {},
          duplicated_with: { "duplicated-material": [], "duplicated-mpn": [] },
          emcr_item_code: item.mcr_item_code,
          current_user: this.currentUsername,
          action_notes: "update_notes",
          emcr_item_note: emcrItemNote,
        };

        const response = await api(this, cataloguerFormService.updateItemStatus(item.mcr_item_code, payload));
        this.applyItemStatusUpdate(response.data);
      } catch (error) {
        const httpStatus = error?.response?.status;
        const serverMsg = error?.response?.data?.message || error?.response?.data?.error;
        const detail = serverMsg || error?.message || "Unknown error";
        const summary = httpStatus ? `Error ${httpStatus}` : "Error";
        this.$toastNotifPrime("error", summary, detail);
      }
    },
    // Dipakai onStatusSaved (dari CheckDuplicateModal) dan onCancelItem. Response updateItemStatus() berisi kolom item
    // versi BE (flat, bukan struktur cat_emcr_item_* FE), jadi item TIDAK ditimpa seluruhnya -- cuma field status yang di-patch.
    applyItemStatusUpdate(data) {
      if (!data?.mcr_item_code) return;
      const item = this.actionItems.find((i) => i.mcr_item_code === data.mcr_item_code);
      if (!item) return;
      const patch = {
        status: data.status,
        catalogued_by: data.catalogued_by,
        emcr_item_note: data.emcr_item_note,
        dupl_material_number: data.dupl_material_number,
      };
      this.$store.commit("mcr/patchMcrItem", { mcr_item_code: data.mcr_item_code, patch });
      if (this.checkDuplicateModal.item?.mcr_item_code === data.mcr_item_code) {
        this.checkDuplicateModal.item = this.toCheckDuplicateItem(item);
      }
    },
  },
};
